import { api } from "@/lib/api";
import { supplierQuantityToMicrounits } from "@shared/supplier-unit-price";
import { formatBrandQuantity } from "@shared/purchase-order-brands";

export type ReceiptSplitGroup = { id: string; itemId: string; poLineId: string | null; unit: string; originalQty: string };
export type ReceiptBrandOptions = {
  brands: { id: string; name: string; nameAr: string; active: boolean; usualSupplierIds: string[] }[];
  suggestedBrandId: string | null;
  suggestedBrandSource: string | null;
  orderedBrandId: string | null;
  orderedBrandName: string | null;
};
export type ReceiptBrandFields = {
  brandId?: string | null; brandSource?: string | null; brandName?: string | null;
  brandNameAr?: string | null; lineKind?: "normal" | "bonus"; splitGroupId?: string | null;
  _splitGroup?: ReceiptSplitGroup; _duplicateAccepted?: boolean;
  _brandContext?: string;
  _brandTouched?: boolean;
};
/** Explicitly reselecting the same brand claims receiver ownership too. */
export function hasReceiptBrandCorrection(line: ReceiptBrandFields, originalBrandId?: string | null) {
  return !!line._brandTouched || (line.brandId || null) !== (originalBrandId || null);
}
export function receiptBrandCorrection(line: ReceiptBrandFields, originalBrandId?: string | null, request = false) {
  return hasReceiptBrandCorrection(line, originalBrandId)
    ? request ? { requestedBrandId: line.brandId || null } : { brandId: line.brandId || null }
    : {};
}
export function hydrateReceiptBrandLine(line: any, receipt: any) {
  return { ...line, _duplicateAccepted: true, _splitGroup: receipt.splitGroups?.find((group: ReceiptSplitGroup) => group.id === line.splitGroupId) || line._splitGroup };
}
export function receiptSplitGroups(lines: any[]): ReceiptSplitGroup[] {
  return [...new Map(lines.filter(line => line.splitGroupId && line._splitGroup).map(line => [line.splitGroupId, line._splitGroup])).values()] as ReceiptSplitGroup[];
}
export function receiptSplitStatus(lines: any[], group: ReceiptSplitGroup) {
  const left = supplierQuantityToMicrounits(group.originalQty) - lines.filter(line => line.splitGroupId === group.id && line.lineKind !== "bonus").reduce((sum, line) => sum + supplierQuantityToMicrounits(line.receivedQty), 0n);
  return { valid: left === 0n, text: left === 0n ? `Adds up to ${group.originalQty} ${group.unit}` : left > 0n ? `${formatBrandQuantity(left)} ${group.unit} left to split` : `${formatBrandQuantity(-left)} ${group.unit} over the delivered quantity` };
}
export function receiptSplitError(lines: any[]) {
  try {
    for (const line of lines) if (line.splitGroupId && !line._splitGroup) return "Reload this receipt to restore its original split quantity.";
    for (const group of receiptSplitGroups(lines)) {
      const status = receiptSplitStatus(lines, group);
      if (!status.valid) return status.text;
      const rows = lines.filter(line => line.splitGroupId === group.id && line.lineKind !== "bonus");
      if (rows.some(line => line.itemId !== group.itemId || line.unit !== group.unit || (line.poLineId || null) !== group.poLineId)) return "Split rows must use the same item, unit and PO line.";
      const brands = rows.map(line => line.brandId).filter(Boolean);
      if (new Set(brands).size !== brands.length) return "Choose a different brand for each split row.";
    }
  } catch { return "Enter valid split quantities with at most 6 decimal places."; }
  return null;
}
export async function validateReceiptBrands(lines: any[], workspaceId: string, supplierId: string, branchId: string) {
  for (const [index, line] of lines.entries()) {
    const options = await api.supplierReceipts.brandOptions(workspaceId, { itemId: line.itemId, supplierId, branchId, poLineId: line.poLineId || "" });
    if (options.brands.some((brand: any) => brand.active) && !line.brandId) throw new Error(`Choose the brand for ${line.itemName} on line ${index + 1}.`);
  }
}
export function groupedReceiptLines<T extends { splitGroupId?: string | null }>(lines: T[]): T[] {
  const seen = new Set<string>();
  return lines.flatMap(line => {
    if (!line.splitGroupId) return [line];
    if (seen.has(line.splitGroupId)) return [];
    seen.add(line.splitGroupId);
    return lines.filter(row => row.splitGroupId === line.splitGroupId);
  });
}
export function receiptBrandLabel(line: any) {
  return [line.brandName, line.brandNameAr, line.lineKind === "bonus" ? "Bonus" : ""].filter(Boolean).join(" · ");
}